import {Box, Grid, Section, SectionIntro} from '@primer/react-brand/esm'
import {FlexSuiteLargeCards} from '../../../components/FlexSuiteLargeCards'
import {FlexSuiteOutroCTA} from '../../../components/FlexSuiteOutroCTA'
import {FlexSuiteOutroCustomerStories} from '../../../components/FlexSuiteOutroCustomerStories'
import {FlexSuiteOutroResources} from '../../../components/FlexSuiteOutroResources'
import {FlexSuiteStatistics} from '../../../components/FlexSuiteStatistics'
import {FlexSuiteTestimonialBanner, FlexSuiteTestimonialQuote} from '../../../components/FlexSuiteTestimonial'
import baseStyles from '../../../styles/base.module.css'
import {isDownloadOptions, isLink} from '../../../types'
import type {OutroSectionProps} from './types'
import {useBrandedDivider} from '../../../hooks/useBrandedDivider'

export function OutroSection({
  outroLargeCards,
  outroTestimonial,
  outroTestimonialLink,
  outroTestimonialLogo,
  outroTestimonialQuote,
  outroTestimonialImage,
  outroTestimonialLabel,
  outroTestimonialName,
  outroTestimonialTitle,
  outroStats,
  outroCustomerStories,
  outroCtaHeading,
  outroCtaDescription,
  outroCtaOne,
  outroCtaTwo,
  outroBrandedDivider,
  outroResourcesHeading,
  outroResources,
}: OutroSectionProps) {
  const hasLargeCards = Boolean(outroLargeCards?.length)
  const hasQuoteTestimonial = outroTestimonial === 'Quote' && Boolean(outroTestimonialQuote)
  const hasBrandBannerTestimonial = outroTestimonial === 'Brand Banner' && Boolean(outroTestimonialQuote)
  const hasStats = Boolean(outroStats?.length)
  const hasCustomerStories = outroCustomerStories && outroCustomerStories.length > 0
  const hasResources = Boolean(outroResources?.length)
  const hasContentBeforeStats = hasLargeCards || hasQuoteTestimonial || hasBrandBannerTestimonial
  const hasCtaBanner = Boolean(
    outroCtaHeading ||
    outroCtaDescription ||
    isDownloadOptions(outroCtaOne) ||
    isDownloadOptions(outroCtaTwo) ||
    (isLink(outroCtaOne) && outroCtaOne.fields.href) ||
    (isLink(outroCtaTwo) && outroCtaTwo.fields.href),
  )
  const {hasBrandedDivider, BrandedDivider} = useBrandedDivider({
    enabled: outroBrandedDivider,
  })
  const ctaNeedsBottomBorder = hasBrandedDivider || hasResources

  if (
    !hasLargeCards &&
    !hasQuoteTestimonial &&
    !hasBrandBannerTestimonial &&
    !hasStats &&
    !hasCustomerStories &&
    !hasCtaBanner &&
    !hasResources &&
    !hasBrandedDivider
  ) {
    return null
  }

  return (
    <Box
      marginBlockStart={hasStats && !hasContentBeforeStats ? {narrow: 'spacious', regular: 64} : 48}
      className={baseStyles.greenBorderBottom}
    >
      <Section fullWidth paddingBlockStart="none" paddingBlockEnd="none">
        {hasLargeCards ? (
          <Box paddingBlockStart={48} paddingBlockEnd={48}>
            <FlexSuiteLargeCards cards={outroLargeCards} />
          </Box>
        ) : null}

        {hasQuoteTestimonial ? (
          <FlexSuiteTestimonialQuote
            quote={outroTestimonialQuote}
            name={outroTestimonialName}
            title={outroTestimonialTitle}
            link={outroTestimonialLink?.fields}
            logo={outroTestimonialLogo}
          />
        ) : null}

        {hasBrandBannerTestimonial ? (
          <Box paddingBlockStart="spacious" paddingBlockEnd="spacious">
            {outroTestimonialLabel ? (
              <Box marginBlockEnd={16}>
                <SectionIntro align="center" fullWidth>
                  <SectionIntro.Label>{outroTestimonialLabel}</SectionIntro.Label>
                </SectionIntro>
              </Box>
            ) : null}
            <FlexSuiteTestimonialBanner
              quote={outroTestimonialQuote}
              link={outroTestimonialLink?.fields}
              logo={outroTestimonialLogo}
              image={outroTestimonialImage}
            />
          </Box>
        ) : null}

        {hasStats ? <FlexSuiteStatistics stats={outroStats} /> : null}

        {hasCustomerStories ? (
          <FlexSuiteOutroCustomerStories variant="icon-cards" stories={outroCustomerStories} />
        ) : null}

        {hasCtaBanner ? (
          <Box paddingBlockStart={{narrow: 'spacious', regular: 64}}>
            <FlexSuiteOutroCTA
              outroCtaHeading={outroCtaHeading}
              outroCtaDescription={outroCtaDescription}
              outroCtaOne={outroCtaOne}
              outroCtaTwo={outroCtaTwo}
              hasBottomBorder={ctaNeedsBottomBorder}
            />
          </Box>
        ) : null}

        {hasBrandedDivider ? (
          <Box className={baseStyles.gridFrameBottom} data-testid="category-outro-branded-divider">
            <Grid>
              <Grid.Column>
                <BrandedDivider />
              </Grid.Column>
            </Grid>
          </Box>
        ) : null}

        {hasResources ? (
          <FlexSuiteOutroResources outroResourcesHeading={outroResourcesHeading} outroResources={outroResources} />
        ) : null}
      </Section>
    </Box>
  )
}
